import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import { OrgProvider } from './context/OrgContext'
import { ZoomOAuthProvider } from './context/ZoomOAuthContext'
import { ToastProvider } from './components/common/Toast'
import ErrorBoundary from './components/common/ErrorBoundary'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ForgotPasswordOtpPage from './pages/ForgotPasswordOtpPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const NewDashboardPage = lazy(() => import('./pages/NewDashboardPage'))
const BaselineReadyPage = lazy(() => import('./pages/BaselineReadyPage'))
const PlatformAdminPage = lazy(() => import('./pages/PlatformAdminPage'))
const SetupBehaviorInspectorPage = lazy(() => import('./pages/SetupBehaviorInspectorPage'))
const LeaderProxyPage = lazy(() => import('./pages/LeaderProxyPage'))
const StrategyPage = lazy(() => import('./pages/StrategyPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'))
const DiagnosticPage = lazy(() => import('./pages/DiagnosticPage'))
const ConnectionsPage = lazy(() => import('./pages/ConnectionsPage'))
const IntegrationV2AdminPage = lazy(() => import('./pages/IntegrationV2AdminPage'))
const DecisionConsolePage = lazy(() => import('./pages/DecisionConsolePage'))
const DecisionIntegrityDemoPage = lazy(() => import('./pages/DecisionIntegrityDemoPage'))
import CalendarPage from './pages/CalendarPage'
import OrgOversightMeetingsPage from './pages/OrgOversightMeetingsPage'
import LegacyAgendaBuilderPage from './pages/AgendaBuilderPage'
import MeetingBuilderPage from './pages/MeetingBuilderPage'
import QuickStartPage from './pages/QuickStartPage'
import VoiceAgendaPage from './pages/VoiceAgendaPage'
import MeetingDetailPage from './pages/MeetingDetailPage'
import ExternalMeetingSetupPage from './pages/ExternalMeetingSetupPage'
import ExternalMeetingBriefPage from './pages/ExternalMeetingBriefPage'
import ChatFirstSetupPage from './pages/ChatFirstSetupPage'
import AnalyticsPage from './pages/AnalyticsPage'
import CompanyAnalysisPage from './pages/CompanyAnalysisPage'
import SettingsPage from './pages/SettingsPage'
import PrivacyPage from './pages/PrivacyPage'
import SecurityWhitepaperPage from './pages/SecurityWhitepaperPage'
import TermsPage from './pages/TermsPage'
import AboutPage from './pages/AboutPage'
import PricingPage from './pages/PricingPage'
import SupportPage from './pages/SupportPage'
import FaqsPage from './pages/FaqsPage'
import WhitepaperContactUs from './pages/WhitepaperContactUs'
import ContactUs from './pages/ContactUs'
import DocumentationPage from './pages/DocumentationPage'
import SalesPage from './pages/SalesPage'
import EngineeringPage from './pages/EngineeringPage'
import LeadershipPage from './pages/LeadershipPage'
import OperationsPage from './pages/OperationsPage'
import FoundersPage from './pages/FoundersPage'
import ExternalEngagementsPage from './pages/ExternalEngagementsPage'
import NotFoundPage from './pages/NotFoundPage'
import JoinPage from './pages/JoinPage'
import ProtectedRoute, { AdminRoute } from './components/common/ProtectedRoute'
import PublicRoute from './components/common/PublicRoute'
import VerifyEmailRoute from './components/common/VerifyEmailRoute'
// StealthGate removed — site is fully public
import MeetingReminder from './components/meeting/MeetingReminder'
import MeetingAlertOverlay from './components/meeting/MeetingAlertOverlay'
import PostFirstMeetingVideo from './components/meeting/PostFirstMeetingVideo'
import FirstMeetingCreatedVideo from './components/meeting/FirstMeetingCreatedVideo'
import ProductAppVideoBoundary from './components/common/ProductAppVideoBoundary'
import AnalyticsRouteListener from './components/AnalyticsRouteListener'
import ScrollToTop from './components/common/ScrollToTop'
import { BillingProvider } from './context/BillingContext'
import { QUICK_START_ROUTE } from './utils/quickStartFlow'
// StealthUnlockFromQuery replaced by StealthGate (wraps entire app)

const CHAT_FIRST_SETUP_ENABLED = import.meta.env.VITE_CHAT_FIRST_SETUP_ENABLED === 'true'
const SETUP_BEHAVIOR_INSPECTOR_ENABLED = import.meta.env.VITE_SETUP_BEHAVIOR_INSPECTOR_ENABLED === 'true'

/** Bare `/admin/setup-behavior` redirects; inspector URLs require `/admin/setup-behavior/:userId`. */
function SetupBehaviorAdminIndexRedirect() {
  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7730/ingest/40704d02-ed97-4c5f-9aec-31ab7628389e', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'eb7a95' },
      body: JSON.stringify({
        sessionId: 'eb7a95',
        location: 'App.jsx:SetupBehaviorAdminIndexRedirect',
        message: 'Bare /admin/setup-behavior matched index route (post-fix)',
        data: {
          hypothesisId: 'VERIFY',
          inspectorFlagOn: SETUP_BEHAVIOR_INSPECTOR_ENABLED,
        },
        timestamp: Date.now(),
        runId: 'post-fix',
      }),
    }).catch(() => {})
    // #endregion
  }, [])
  return SETUP_BEHAVIOR_INSPECTOR_ENABLED ? (
    <Navigate to="/platform-admin" replace />
  ) : (
    <Navigate to="/dashboard" replace />
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <OrgProvider>
        <BillingProvider>
        <ZoomOAuthProvider>
          <ToastProvider>
            <div className="min-h-screen bg-white">
            <ScrollToTop />
            <AnalyticsRouteListener />
            <MeetingReminder />
            <MeetingAlertOverlay />
            <ProductAppVideoBoundary>
              <PostFirstMeetingVideo />
              <FirstMeetingCreatedVideo />
            </ProductAppVideoBoundary>
            <Routes>
              {/* Public Routes - Redirect authenticated users */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } />
              <Route path="/signup" element={
                <PublicRoute>
                  <SignupPage />
                </PublicRoute>
              } />
              <Route path="/join" element={<JoinPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/forgot-password/verify" element={<ForgotPasswordOtpPage />} />
              <Route path="/forgot-password/reset" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={
                <VerifyEmailRoute>
                  <VerifyEmailPage />
                </VerifyEmailRoute>
              } />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/faqs" element={<FaqsPage />} />
              <Route path="/whitepaper-contact" element={<WhitepaperContactUs />} />
              <Route path="/documentation" element={<DocumentationPage />} />

              {/* Legal & company pages - Public, no redirect for authenticated users */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/sales" element={<SalesPage />} />
              <Route path="/engineering" element={<EngineeringPage />} />
              <Route path="/external-engagements" element={<ExternalEngagementsPage />} />
              <Route path="/leadership" element={<LeadershipPage />} />
              <Route path="/operations" element={<OperationsPage />} />
              <Route path="/founders" element={<FoundersPage />} />
              {/* Founding team page disabled — stub: FoundingTeamPage.jsx; re-add import + route to enable */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/security-ai-privacy-whitepaper" element={<SecurityWhitepaperPage />} />
              <Route path="/terms" element={<TermsPage />} />

              {/* Protected Routes - Require auth */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <DashboardPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/home" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <NewDashboardPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/baseline-ready" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <BaselineReadyPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/calendar" element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              } />
              <Route path="/org-oversight" element={
                <ProtectedRoute>
                  <OrgOversightMeetingsPage />
                </ProtectedRoute>
              } />
              <Route path="/scorecard" element={
                <ProtectedRoute>
                  <Navigate to="/analytics" replace />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              } />
              <Route path="/strategy" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <StrategyPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/search" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <SearchPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/meetings" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <MeetingsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/company-analysis" element={
                <ProtectedRoute>
                  <CompanyAnalysisPage />
                </ProtectedRoute>
              } />
              <Route path="/diagnostics" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-zinc-900"><div className="animate-pulse text-zinc-400">Loading...</div></div>}>
                    <DiagnosticPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/connections" element={
                <ProtectedRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <ConnectionsPage />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/join-external" element={
                <ProtectedRoute>
                  <ExternalMeetingSetupPage />
                </ProtectedRoute>
              } />
              <Route path="/join-external/brief/:meetingId" element={
                <ProtectedRoute>
                  <ExternalMeetingBriefPage />
                </ProtectedRoute>
              } />
              <Route path="/setup" element={
                <ProtectedRoute>
                  {CHAT_FIRST_SETUP_ENABLED ? <ChatFirstSetupPage /> : <Navigate to="/dashboard" replace />}
                </ProtectedRoute>
              } />
              <Route path={QUICK_START_ROUTE} element={
                <ProtectedRoute>
                  <QuickStartPage />
                </ProtectedRoute>
              } />
              <Route path="/leader-proxy" element={
                <AdminRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <LeaderProxyPage />
                  </Suspense>
                </AdminRoute>
              } />
              <Route path="/agenda-legacy/:meetingId?" element={
                <ProtectedRoute>
                  <LegacyAgendaBuilderPage />
                </ProtectedRoute>
              } />
              <Route path="/agenda/:meetingId?" element={
                <ProtectedRoute>
                  <MeetingBuilderPage />
                </ProtectedRoute>
              } />
              <Route path="/agenda/voice/:meetingId?" element={
                <ProtectedRoute>
                  <VoiceAgendaPage />
                </ProtectedRoute>
              } />
              <Route path="/meeting/:meetingId" element={
                <ProtectedRoute>
                  <MeetingDetailPage />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/platform-admin" element={
                <AdminRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                    <PlatformAdminPage />
                  </Suspense>
                </AdminRoute>
              } />
              <Route path="/admin/integrations-v2" element={
                <AdminRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-zinc-900"><div className="animate-pulse text-zinc-400">Loading...</div></div>}>
                    <IntegrationV2AdminPage />
                  </Suspense>
                </AdminRoute>
              } />
              <Route path="/console" element={
                <AdminRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading…</div></div>}>
                    <DecisionConsolePage />
                  </Suspense>
                </AdminRoute>
              } />
              <Route path="/admin/setup-behavior" element={
                <AdminRoute>
                  <SetupBehaviorAdminIndexRedirect />
                </AdminRoute>
              } />
              <Route path="/admin/setup-behavior/:userId" element={
                <AdminRoute>
                  {SETUP_BEHAVIOR_INSPECTOR_ENABLED ? (
                    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
                      <SetupBehaviorInspectorPage />
                    </Suspense>
                  ) : <Navigate to="/dashboard" replace />}
                </AdminRoute>
              } />
              {/* Decision Integrity Demo — full-screen pitch surface (API endpoints have their own auth) */}
              <Route path="/demo/decision-integrity" element={
                <AdminRoute>
                  <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-zinc-900"><div className="animate-pulse text-zinc-400">Loading...</div></div>}>
                    <DecisionIntegrityDemoPage />
                  </Suspense>
                </AdminRoute>
              } />
              {/* 404 Fallback */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </div>
          </ToastProvider>
        </ZoomOAuthProvider>
        </BillingProvider>
        </OrgProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
